import { trackAdActivity } from "../../utils/adAnalytics";
import { useParams, useHistory, Link } from "react-router-dom";
import { useCallback, useEffect, useRef, useState } from "react";
import { supabase } from "../../supabase";
import STLViewer from "../../components/STLViewer";
import useAuth from "../../hooks/useAuth";
import { useTranslation } from "react-i18next";
import GalleryCard from "./GalleryCard";
import { saveOrderStl } from "../../utils/stlHandoff";
import "./GalleryDetail.css";

// 원본 SVG 는 캔버스 여백이 커서 그대로 띄우면 그림이 작게 보인다 →
// 실제 선이 있는 영역(getBBox)으로 viewBox 를 잘라 화면을 채운다. 실패하면 원본 그대로.
function TrimmedDrawing({ url, alt }) {
  const [src, setSrc] = useState(url);
  useEffect(() => {
    let alive = true;
    let objUrl = null;
    setSrc(url);
    fetch(url)
      .then((r) => (r.ok ? r.text() : Promise.reject()))
      .then((txt) => {
        if (!alive) return;
        const svg = new DOMParser().parseFromString(txt, "image/svg+xml").documentElement;
        if (!svg || svg.nodeName.toLowerCase() !== "svg") return;
        const host = document.createElement("div");
        host.style.cssText = "position:absolute;left:-99999px;top:0;visibility:hidden";
        const live = document.importNode(svg, true);
        host.appendChild(live);
        document.body.appendChild(host);
        let b;
        try { b = live.getBBox(); } finally { document.body.removeChild(host); }
        if (!b || !b.width || !b.height) return;
        const pad = Math.max(b.width, b.height) * 0.04; // 선 두께 몫
        svg.setAttribute("viewBox", `${b.x - pad} ${b.y - pad} ${b.width + pad * 2} ${b.height + pad * 2}`);
        // 크기를 안 주면 img 가 300×150 기본값으로 잡혀 비율이 깨진다
        svg.setAttribute("width", b.width + pad * 2);
        svg.setAttribute("height", b.height + pad * 2);
        svg.setAttribute("preserveAspectRatio", "xMidYMid meet");
        const blob = new Blob([new XMLSerializer().serializeToString(svg)], { type: "image/svg+xml" });
        objUrl = URL.createObjectURL(blob);
        setSrc(objUrl);
      })
      .catch(() => {});
    return () => {
      alive = false;
      if (objUrl) URL.revokeObjectURL(objUrl);
    };
  }, [url]);
  return <img src={src} alt={alt} />;
}
import { useModal } from "../../context/ModalContext";

const API_BASE = process.env.REACT_APP_API_BASE_URL || "";

/* 뷰어 배경 = 갤러리 접시 색 (2026-10-16 리뉴얼) */
const PLATE_BG = 0xF4E5D6;
/* 갤러리 썸네일·3D 보기 = 크림 배경 위 캐러멜 커터, 확대 (2026-10-16 김현비 — 기존 23개 썸네일과 같은 모양) */
const CUTTER_COLOR = 0xD9A066;
const CUTTER_ZOOM = 1.55;

function fmtDate(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}.${pad(d.getMonth() + 1)}.${pad(d.getDate())}`;
}

function fmtMm(n) {
  return String(Math.round(n * 10) / 10);
}

export default function GalleryDetail() {
  const { id } = useParams();
  const history = useHistory();
  const { t } = useTranslation();
  const { user } = useAuth();
  const { showAlert, showConfirm } = useModal();

  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [rejectModal, setRejectModal] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [liked, setLiked] = useState(false);
  const [likeCount, setLikeCount] = useState(0);
  const [likeAnimating, setLikeAnimating] = useState(false);
  const [bounds, setBounds] = useState(null);
  const [related, setRelated] = useState([]);
  const [printing, setPrinting] = useState(false);
  const [showDrawing, setShowDrawing] = useState(false);

  // 비회원 하트 안내 말풍선 (꼬리가 하트 배지 가로 가운데를 가리키게 실측)
  const [guestToast, setGuestToast] = useState(null);
  const toastTimerRef = useRef(null);
  const heartRef = useRef(null);
  const infoRef = useRef(null);

  useEffect(() => {
    async function fetchPost() {
      setLoading(true);
      setBounds(null);
      const { data, error } = await supabase
        .from("gallery")
        .select(`
          *,
          profiles (
            username
          )
        `)
        .eq("id", id)
        .single();

      if (!error) {
        setPost(data);
        setLikeCount(data?.like_count || 0);
      } else {
        setPost(null);
      }
      setLoading(false);
    }

    fetchPost();
    window.scrollTo(0, 0);
  }, [id]);

  useEffect(() => {
    setIsAdmin(user?.role === "admin");
  }, [user?.role]);

  useEffect(() => {
    if (!user?.id || !id) return;
    supabase
      .from("gallery_likes")
      .select("id")
      .eq("user_id", user.id)
      .eq("gallery_id", id)
      .maybeSingle()
      .then(({ data }) => setLiked(!!data));
  }, [user?.id, id]);

  useEffect(() => () => clearTimeout(toastTimerRef.current), []);

  /* ===== 비슷한 쿠키커터: 태그가 하나라도 겹치는 승인 글, 겹치는 수가 많은 순 4개 ===== */
  const postTagsKey = (post?.tags || []).join("\u0001");
  useEffect(() => {
    const tags = postTagsKey ? postTagsKey.split("\u0001") : [];
    if (!post?.id || tags.length === 0) {
      setRelated([]);
      return;
    }
    let cancelled = false;
    supabase
      .from("gallery")
      .select(`
        *,
        profiles (
          username
        )
      `)
      .eq("status", "approved")
      .neq("id", post.id)
      .overlaps("tags", tags)
      .order("created_at", { ascending: false })
      .limit(40)
      .then(({ data, error }) => {
        if (cancelled || error || !data) return;
        const tagSet = new Set(tags);
        const scored = data
          .map((p) => ({ p, s: (p.tags || []).filter((tg) => tagSet.has(tg)).length }))
          .sort((a, b) => b.s - a.s)
          .slice(0, 4)
          .map((x) => x.p);
        setRelated(scored);
      });
    return () => { cancelled = true; };
  }, [post?.id, postTagsKey]);

  /* ===== 썸네일 백필 (기존 로직 그대로 — 썸네일 없는 글만 저장) ===== */
  const postId = post?.id;
  const postThumb = post?.thumbnail_url;
  const onThumbnail = useCallback(async (dataUrl) => {

    console.log("🔥 썸네일 생성 시도됨");
    if (postThumb) return;
    const fileName = `thumb-${postId}.png`;

    // base64 → blob 변환
    const res = await fetch(dataUrl);
    const blob = await res.blob();

    // Storage 업로드
    const { error } = await supabase.storage
      .from("thumbnails")
      .upload(fileName, blob, { upsert: true });

    if (error) {
      console.error("썸네일 업로드 실패", error);
      return;
    }

    const { data: publicUrl } = supabase.storage
      .from("thumbnails")
      .getPublicUrl(fileName);

    // DB 업데이트
    await supabase
      .from("gallery")
      .update({ thumbnail_url: publicUrl.publicUrl })
      .eq("id", postId);

    console.log("썸네일 저장 완료");
  }, [postId, postThumb]);

  const onBounds = useCallback((b) => setBounds(b), []);

  function showGuestToast() {
    const heart = heartRef.current;
    const info = infoRef.current;
    if (!heart || !info) return;
    const hr = heart.getBoundingClientRect();
    const ir = info.getBoundingClientRect();
    setGuestToast({
      top: hr.bottom - ir.top + 12,
      arrowRight: ir.right - (hr.left + hr.width / 2),
    });
    clearTimeout(toastTimerRef.current);
    toastTimerRef.current = setTimeout(() => setGuestToast(null), 2500);
  }

  async function toggleLike() {
    if (!user?.id) {
      showGuestToast();
      return;
    }
    if (likeAnimating) return;

    setLikeAnimating(true);
    setTimeout(() => setLikeAnimating(false), 400);

    if (liked) {
      setLiked(false);
      setLikeCount((c) => Math.max(0, c - 1));
      await supabase
        .from("gallery_likes")
        .delete()
        .eq("user_id", user.id)
        .eq("gallery_id", id);
    } else {
      setLiked(true);
      setLikeCount((c) => c + 1);
      await supabase
        .from("gallery_likes")
        .insert({ user_id: user.id, gallery_id: id });
    }
  }

  /* ===== STL 다운로드 ===== */
  async function downloadStl() {
    const res = await fetch(post.stl_url);
    const blob = await res.blob();

    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = post.stl_filename || "model.stl";
    document.body.appendChild(a);
    a.click();
    trackAdActivity("stl_download");
    a.remove();
    window.URL.revokeObjectURL(url);

    const newCount = (post.download_count || 0) + 1;

    // 1. gallery download_count 증가
    await supabase
      .from("gallery")
      .update({ download_count: newCount })
      .eq("id", post.id);

    // 2. download_logs에 기록
    await supabase
      .from("download_logs")
      .insert({
        user_id: user?.id || null,
        gallery_id: post.id,
      });

    // 3. 화면 반영
    setPost({ ...post, download_count: newCount });
  }

  /* ===== 3D프린팅으로 받아보기: 쿠키커터 만들기 주문하기와 같은 IndexedDB 형식으로 넘김 ===== */
  async function goOrder() {
    if (!post?.stl_url || printing) return;
    setPrinting(true);
    try {
      const res = await fetch(post.stl_url);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const base = String(post.stl_filename || post.title || "cookistory").replace(/\.stl$/i, "");
      await saveOrderStl(blob, `${base}.stl`);
      history.push("/order");
    } catch (err) {
      console.error(err);
      showAlert(t("galleryUi.printFailed"));
    } finally {
      setPrinting(false);
    }
  }

  /* ===== 삭제 (✅ 정상 방식) ===== */
  async function deletePost() {
    if (!(await showConfirm("정말 삭제하시겠습니까?"))) return;

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        showAlert("로그인이 필요합니다.");
        return;
      }

      const res = await fetch(
        `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/delete-gallery-post`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${session.access_token}`,
            apikey: process.env.REACT_APP_SUPABASE_ANON_KEY,
          },
          body: JSON.stringify({ post_id: post.id }),
        }
      );

      if (!res.ok) {
        const text = await res.text();
        throw new Error(text || "삭제 실패");
      }

      history.push("/gallery");
    } catch (err) {
      console.error(err);
      showAlert("삭제 실패: " + err.message);
    }
  }

  if (loading) {
    return (
      <div className="gd-page">
        <div className="gd-wrap gd-msg">{t("galleryUi.loading")}</div>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="gd-page">
        <div className="gd-wrap gd-msg">{t("galleryUi.notFound")}</div>
      </div>
    );
  }

  const isOwner = post.user_id === user?.id;

  // 비승인 글 접근 제어: 본인 또는 admin만 볼 수 있음
  if (post.status && post.status !== "approved" && !isOwner && !isAdmin) {
    return (
      <div className="gd-page">
        <div className="gd-wrap gd-msg">{t("galleryUi.notFound")}</div>
      </div>
    );
  }

  const author = post.profiles?.username || "-";
  const tags = post.tags || [];

  return (
    <div className="gd-page">
      <div className="gd-wrap">
        {/* 승인 상태 배너 */}
        {post.status === "pending" && isOwner && (
          <div className="gd-status-banner pending">
            승인 대기 중입니다. 관리자 승인 후 갤러리에 공개됩니다.
          </div>
        )}
        {post.status === "rejected" && isOwner && (
          <div className="gd-status-banner rejected">
            거절된 게시글입니다.
            {post.reject_reason && <span> (사유: {post.reject_reason})</span>}
          </div>
        )}
        {post.status === "hidden" && isAdmin && (
          <div className="gd-status-banner rejected">
            숨겨진 게시글입니다. (관리자에게만 표시)
          </div>
        )}

        <div className="gd-crumb">
          <Link to="/gallery">{t("galleryUi.crumb")}</Link> › <b>{post.title}</b>
        </div>

        <div className="gd-grid">
          <div className="gd-viewer">
            {post.stl_url && post.svg_url && (
              <div className="gd-vt" role="tablist">
                <button type="button" role="tab" aria-selected={!showDrawing} className={showDrawing ? "" : "on"} onClick={() => setShowDrawing(false)}>
                  {t("galleryUi.view3d")}
                </button>
                <button type="button" role="tab" aria-selected={showDrawing} className={showDrawing ? "on" : ""} onClick={() => setShowDrawing(true)}>
                  {t("galleryUi.viewDrawing")}
                </button>
              </div>
            )}
            {showDrawing && post.svg_url && (
              <div className="gd-drawing">
                <TrimmedDrawing url={post.svg_url} alt={post.title || ""} />
              </div>
            )}
            {post.stl_url ? (
              <STLViewer
                url={post.stl_url}
                hideSize
                bgColor={PLATE_BG}
                modelColor={CUTTER_COLOR}
                zoom={CUTTER_ZOOM}
                onThumbnail={onThumbnail}
                onBounds={onBounds}
                bodyBoundsOnly
              />
            ) : (
              post.thumbnail_url && <img src={post.thumbnail_url} alt={post.title || ""} />
            )}
          </div>

          <div className="gd-info" ref={infoRef}>
            <div className="gd-card">
              <div className="gd-pt">
                <h1>{post.title}</h1>
                <button
                  type="button"
                  ref={heartRef}
                  className={`gd-heart ${liked ? "liked" : ""} ${likeAnimating ? "pop" : ""} ${guestToast ? "show" : ""}`}
                  onClick={toggleLike}
                  aria-label={t("galleryUi.like")}
                >
                  {liked ? "♥" : "♡"} <b>{likeCount}</b>
                </button>
              </div>

              <div className="gd-meta">
                <span className="gd-av">{author.charAt(0)}</span>
                <b>{author}</b>
                <i>·</i>
                {fmtDate(post.created_at)}
                <i>·</i>
                {t("galleryUi.downloadsN", { count: post.download_count || 0 })}
              </div>

              {post.description && <p className="gd-desc">{post.description}</p>}

              {(bounds || tags.length > 0) && (
                <dl className="gd-kv">
                  {bounds && (
                    <>
                      <dt>{t("galleryUi.size")}</dt>
                      <dd>
                        {t("galleryUi.sizeValue", {
                          w: fmtMm(bounds.x),
                          d: fmtMm(bounds.z),
                          h: fmtMm(bounds.y),
                        })}
                      </dd>
                    </>
                  )}
                  {tags.length > 0 && (
                    <>
                      <dt>{t("galleryUi.tags")}</dt>
                      <dd className="gd-tg">
                        {tags.map((kw) => (
                          <Link key={kw} to={`/gallery?tag=${encodeURIComponent(kw)}`}>
                            #{kw}
                          </Link>
                        ))}
                      </dd>
                    </>
                  )}
                </dl>
              )}

              <button type="button" className="gd-btn car big" onClick={downloadStl} disabled={!post.stl_url}>
                {t("galleryUi.downloadBtn")}
              </button>
              <button type="button" className="gd-btn ol" onClick={goOrder} disabled={!post.stl_url || printing}>
                {t("galleryUi.printBtn")}
              </button>

              {post.svg_url && (
                <Link className="gd-rm" to={`/cutter?gallery=${post.id}`}>
                  {t("galleryUi.remakeBtn")}
                </Link>
              )}
            </div>

            {guestToast && (
              <div
                className="gd-toast"
                style={{ top: guestToast.top, "--ar": `${guestToast.arrowRight}px` }}
              >
                {t("galleryUi.likeLoginToast")}
                <button type="button" onClick={() => history.push("/login")}>
                  {t("galleryUi.login")}
                </button>
              </div>
            )}

            {/* 관리자·작성자 버튼 (기존 기능 유지) */}
            {(isAdmin || isOwner) && (
              <div className="gd-admin">
                {isAdmin && (
                  <button
                    className="btn"
                    onClick={() =>
                      history.push(`/gallery/write?id=${post.id}`)
                    }
                  >
                    수정
                  </button>
                )}
                {isAdmin && post.status !== "approved" && (
                  <button
                    className="btn btn-outlined btn-outlined-green"
                    onClick={async () => {
                      const { data: { session } } = await supabase.auth.getSession();
                      const res = await fetch(`${API_BASE}/admin/update-gallery-status`, {
                        method: "POST",
                        headers: { "Content-Type": "application/json", Authorization: `Bearer ${session.access_token}` },
                        body: JSON.stringify({ post_ids: [post.id], status: "approved" }),
                      });
                      if (!res.ok) { showAlert("승인 실패"); return; }
                      setPost({ ...post, status: "approved" });
                    }}
                  >
                    승인
                  </button>
                )}
                {isAdmin && post.status !== "hidden" && (
                  <button
                    className="btn btn-outlined btn-outlined-orange"
                    onClick={async () => {
                      if (!(await showConfirm("이 게시글을 숨기겠습니까? 갤러리 목록에서 사라집니다."))) return;
                      const { data: { session } } = await supabase.auth.getSession();
                      const res = await fetch(`${API_BASE}/admin/update-gallery-status`, {
                        method: "POST",
                        headers: { "Content-Type": "application/json", Authorization: `Bearer ${session.access_token}` },
                        body: JSON.stringify({ post_ids: [post.id], status: "hidden" }),
                      });
                      if (!res.ok) { showAlert("숨기기 실패"); return; }
                      setPost({ ...post, status: "hidden" });
                    }}
                  >
                    숨기기
                  </button>
                )}
                {isAdmin && post.status === "hidden" && (
                  <button
                    className="btn btn-outlined btn-outlined-green"
                    onClick={async () => {
                      const { data: { session } } = await supabase.auth.getSession();
                      const res = await fetch(`${API_BASE}/admin/update-gallery-status`, {
                        method: "POST",
                        headers: { "Content-Type": "application/json", Authorization: `Bearer ${session.access_token}` },
                        body: JSON.stringify({ post_ids: [post.id], status: "approved" }),
                      });
                      if (!res.ok) { showAlert("숨기기 해제 실패"); return; }
                      setPost({ ...post, status: "approved" });
                    }}
                  >
                    숨기기 해제
                  </button>
                )}
                {isAdmin && post.status !== "rejected" && (
                  <button
                    className="btn btn-outlined btn-outlined-orange"
                    onClick={() => {
                      setRejectReason("");
                      setRejectModal(true);
                    }}
                  >
                    거절
                  </button>
                )}
                <button
                  className="btn btn-outlined btn-outlined-red"
                  onClick={deletePost}
                >
                  삭제
                </button>
              </div>
            )}
          </div>
        </div>

        {related.length > 0 && (
          <section className="gl-related">
            <h2 className="gd-h2">{t("galleryUi.similar")}</h2>
            <div className="gl-grid">
              {related.map((p) => (
                <GalleryCard key={p.id} post={p} />
              ))}
            </div>
          </section>
        )}
      </div>

      {rejectModal && (
        <div className="gd-modal-overlay" onClick={() => setRejectModal(false)}>
          <div className="gd-modal" onClick={(e) => e.stopPropagation()}>
            <h3>거절 사유</h3>
            <textarea
              placeholder="거절 사유를 입력하세요 (선택)"
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
            />
            <div className="gd-modal-actions">
              <button onClick={() => setRejectModal(false)}>
                취소
              </button>
              <button
                className="gd-reject-btn"
                onClick={async () => {
                  const { data: { session } } = await supabase.auth.getSession();
                  const res = await fetch(`${API_BASE}/admin/update-gallery-status`, {
                    method: "POST",
                    headers: { "Content-Type": "application/json", Authorization: `Bearer ${session.access_token}` },
                    body: JSON.stringify({ post_ids: [post.id], status: "rejected", reject_reason: rejectReason || null }),
                  });
                  if (!res.ok) { showAlert("거절 처리 실패"); return; }
                  setPost({ ...post, status: "rejected", reject_reason: rejectReason });
                  setRejectModal(false);
                }}
              >
                거절 확정
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
